import { isSameProductName, scrollToEle } from '@/utils';
import { inject, observer } from 'mobx-react';
import { useCallback, useMemo, useRef } from 'react';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';
import VersionItem from '../VersionItem/VersionItem';
import styles from './index.module.less';
import BundleExtraModule from './BundleExtraModule';

/**
 * 规格、属性 list
 * @param type VersionConfig.groupType  --specification(规格) | attribute(属性)
 * @param isSpecificationGroup
 * @param isAttributeGroup
 * @param groupList 属性都使用props中的groupList
 * @param itemIndex 属性的title和desc可能是数组，使用index获得正确的索引
 * @param isTopAttrList 是不是模仿成规格的属性列表，表现出规格的特性（将属性列表提前 go-3s）
 */
export default inject(
  'product',
  'area',
  'app'
)(
  observer((props) => {
    const {
      productDetail: { VersionConfig = {}, is_camera, id: pid },
      curSelectCommodity: { id: cid },
      curSelectCommodity,
      setStateValue,
    } = props.product;
    const { LANG } = props.area;
    const { type, groupList, isAttributeGroup, isTopAttrList, itemIndex, isSpecificationGroup } = props;

    const versionListRef = useRef(null);

    // 是否是规格样式
    const isSpecStyle = isSpecificationGroup || (isAttributeGroup && isTopAttrList);

    // 列表标题
    const title = useMemo(() => {
      if (Array.isArray(VersionConfig.versionTitle)) {
        return VersionConfig.versionTitle?.[itemIndex];
      }
      return VersionConfig.versionTitle;
    }, [VersionConfig.versionTitle, itemIndex, pid]);

    // 列表下方描述
    const desc = useMemo(() => {
      if (Array.isArray(VersionConfig.versionDesc)) {
        return VersionConfig.versionDesc?.[itemIndex];
      }
      return VersionConfig.versionDesc;
    }, [VersionConfig.versionDesc, itemIndex, pid]);

    // 当前列表数据源
    const curGroupList = useMemo(() => {
      if (isAttributeGroup) {
        return groupList;
      }
      return VersionConfig.groupList;
    }, [VersionConfig.groupList, isAttributeGroup, groupList]);

    // 选择规格
    const onSelectVersion = useCallback(
      (cid) => {
        setStateValue('curSelectCommodityId', cid);
      },
      [setStateValue]
    );

    // 选择属性
    const onSelectAttribute = useCallback(
      (cid) => {
        const newVersion = curGroupList.find((version) => version.cids.includes(cid));
        // const commodity = newVersion.list.find((d) => isSameProductName(d.commodityName, commodityName, 'equal'));
        // 排除当前属性列表的条件，默认条件是被选中的。eg: ['标准','白', '32g']，当前项是颜色，直接删除白 = ['标准','32g']
        let versionName = curSelectCommodity?.versionName?.filter((val, index) => index !== itemIndex + 1);
        let commodity = newVersion.list.find((d) => {
          const dVersionNames = d.versionName?.filter((val, index) => index !== itemIndex + 1);
          return versionName.every((name, index) => isSameProductName(dVersionNames[index], name, 'equal'));
        });
        if (newVersion && commodity) {
          setStateValue('curSelectCommodityId', commodity.id);
        } else {
          onSelectVersion(cid);
        }
      },
      [curGroupList, curSelectCommodity, itemIndex, onSelectVersion, setStateValue]
    );

    const onSelectCommodity = useCallback(
      (cid) => {
        if (props.isAttributeGroup) {
          onSelectAttribute(cid);
        } else {
          onSelectVersion(cid);
        }

        // 虚拟属性不进行定位
        if (!VersionConfig.noTriggerVersionScroll) {
          setTimeout(() => scrollToEle(versionListRef.current), 50);
        }
      },
      [onSelectAttribute, onSelectVersion, props.isAttributeGroup]
    );

    // 获取默认套餐
    const defaultCommodity = useCallback(
      (version) => {
        if (isSpecificationGroup) {
          return version.list[0];
        }
        // 排除当前属性列表的条件，默认条件是被选中的。eg: ['标准','白', '32g']，当前项是颜色，直接删除白 = ['标准','32g']
        let versionName = curSelectCommodity?.versionName?.filter((val, index) => index !== itemIndex + 1);
        let commodity = version.list.find((d) => {
          const dVersionNames = d.versionName?.filter((val, index) => index !== itemIndex + 1);
          return versionName.every((name, index) => {
            // 第一项是套餐，不需要处理
            if (index === 0) {
              return true;
            }
            return isSameProductName(dVersionNames[index], name, 'equal');
          });
        });
        return commodity || version.list[0];
      },
      [curSelectCommodity?.versionName, isSpecificationGroup, itemIndex]
    );

    // 当前是否为最后一个属性列表
    const isLastAttrList = useMemo(() => {
      if (!isAttributeGroup) {
        return false;
      }
      return itemIndex + 1 === VersionConfig.attrList?.length;
    }, [VersionConfig.attrList?.length, isAttributeGroup, itemIndex]);

    if (VersionConfig?.type !== 'group') return null;
    if (type !== VersionConfig.groupType) return null;

    return (
      <div
        className={styles.versionList}
        data-accessory={!is_camera}
        data-group-type={VersionConfig.groupType}
        data-pid={pid}
        ref={versionListRef}
      >
        <h2>
          <div className={styles.title}>
            {VersionConfig.versionTitleTag && (
              <span className={styles.versionTag}>{VersionConfig.versionTitleTag}</span>
            )}
            {title ?? LANG['productNew.version_title']}
          </div>
        </h2>
        {desc && <div className={styles.desc}>{desc}</div>}
        <div className={styles.content} data-items={VersionConfig.groupStyle?.itemCountInRow ?? ''}>
          {curGroupList?.map((version) => {
            return (
              <ComponentsErrorBoundary
                key={version.versionName}
                name='product VersionList VersionItem'
                data={{ version }}
              >
                <VersionItem
                  key={version.versionName}
                  onChangeCommodity={onSelectCommodity}
                  commodity={defaultCommodity(version)}
                  version={version}
                  isLastAttrList={isLastAttrList}
                  className={styles.item}
                  labels={version.tagInfosGroup}
                  isSpecStyle={isSpecStyle}
                />
              </ComponentsErrorBoundary>
            );
          })}
        </div>

        <BundleExtraModule pid={pid} cid={cid} LANG={LANG} />
      </div>
    );
  })
);
